import React, { useEffect, useRef, useState } from 'react';
import { Navigation, Clock } from 'lucide-react';
import { loadGoogleMaps, HAS_GOOGLE_MAPS } from '../lib/googleMaps';
import { formatDuration } from '../lib/formatDuration';

/**
 * Google Maps route preview between pickup and dropoff with driving directions.
 *
 * Props:
 *   pickup:           { lat, lng, label? }   (required)
 *   dropoff:          { lat, lng, label? }   (required — treated as final stop)
 *   waypoints:        optional [{ lat, lng, label? }] — intermediate stops
 *                     plotted as numbered green pins, included in the route.
 *   distanceKm:       optional pre-computed distance
 *   durationMin:      optional pre-computed duration
 *   geometry:         optional encoded polyline (Google polyline encoding).
 *                     NOTE: ignored when `waypoints` is non-empty so we always
 *                     recompute a true multi-stop route.
 *   onRouteComputed:  ({ distance_km, duration_min, route_geometry }) => void
 *   height:           CSS height (default 240px)
 */
export const RouteMap = ({
  pickup,
  dropoff,
  waypoints = [],
  distanceKm,
  durationMin,
  geometry,
  onRouteComputed,
  height = '240px',
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const polylineRef = useRef(null);
  const markersRef = useRef([]);
  const [stats, setStats] = useState({
    distanceKm: distanceKm ?? null,
    durationMin: durationMin ?? null,
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!HAS_GOOGLE_MAPS || !containerRef.current) return undefined;
    if (!pickup?.lat || !pickup?.lng || !dropoff?.lat || !dropoff?.lng) return undefined;

    loadGoogleMaps()
      .then(() => {
        if (!alive) return;
        // eslint-disable-next-line no-undef
        const g = google;

        // Initial map
        if (!mapRef.current) {
          mapRef.current = new g.maps.Map(containerRef.current, {
            center: { lat: pickup.lat, lng: pickup.lng },
            zoom: 9,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            gestureHandling: 'cooperative', // single-finger scrolls page on mobile
          });
        }

        const map = mapRef.current;

        // Custom A/B markers (matching Mapbox visual style for continuity).
        const pickupIcon = makeMarkerIcon('#2E7D32', 'A');
        const dropoffIcon = makeMarkerIcon('#111827', 'B');

        // Clear any old markers + polyline + directions
        markersRef.current.forEach((m) => m.setMap(null));
        markersRef.current = [];
        if (polylineRef.current) {
          polylineRef.current.setMap(null);
          polylineRef.current = null;
        }
        if (directionsRendererRef.current) {
          directionsRendererRef.current.setMap(null);
          directionsRendererRef.current = null;
        }

        const m1 = new g.maps.Marker({
          position: { lat: pickup.lat, lng: pickup.lng },
          map,
          icon: pickupIcon,
          title: pickup.label || 'Pickup',
        });
        const m2 = new g.maps.Marker({
          position: { lat: dropoff.lat, lng: dropoff.lng },
          map,
          icon: dropoffIcon,
          title: dropoff.label || 'Drop-off',
        });
        markersRef.current = [m1, m2];

        // Plot intermediate waypoints (multi-drop) as green numbered pins.
        // Numbering runs 2..N+1 so labels read Drop 2, Drop 3, … with the
        // final `dropoff` prop being Drop N+2. This matches customer UI.
        const wpList = (waypoints || []).filter((w) => w && w.lat != null && w.lng != null);
        wpList.forEach((wp, idx) => {
          const number = idx + 2; // Drop 2 for first waypoint, etc.
          const wpMarker = new g.maps.Marker({
            position: { lat: wp.lat, lng: wp.lng },
            map,
            icon: makeMarkerIcon('#2E7D32', String(number)),
            title: wp.label || `Drop ${number}`,
          });
          markersRef.current.push(wpMarker);
        });

        // Fit bounds (include every stop)
        const bounds = new g.maps.LatLngBounds();
        bounds.extend({ lat: pickup.lat, lng: pickup.lng });
        bounds.extend({ lat: dropoff.lat, lng: dropoff.lng });
        wpList.forEach((wp) => bounds.extend({ lat: wp.lat, lng: wp.lng }));
        map.fitBounds(bounds, 60);

        // With waypoints we always recompute through DirectionsService so the
        // multi-leg route + totals are accurate.
        const useStoredGeometry = geometry && g.maps.geometry?.encoding && wpList.length === 0;
        if (useStoredGeometry) {
          const path = g.maps.geometry.encoding.decodePath(geometry);
          polylineRef.current = new g.maps.Polyline({
            path,
            geodesic: true,
            strokeColor: '#2E7D32',
            strokeOpacity: 0.85,
            strokeWeight: 4,
            map,
          });
        } else {
          const ds = new g.maps.DirectionsService();
          const dr = new g.maps.DirectionsRenderer({
            map,
            suppressMarkers: true, // we render our own markers
            polylineOptions: {
              strokeColor: '#2E7D32',
              strokeOpacity: 0.85,
              strokeWeight: 4,
            },
          });
          directionsRendererRef.current = dr;

          ds.route(
            {
              origin: { lat: pickup.lat, lng: pickup.lng },
              destination: { lat: dropoff.lat, lng: dropoff.lng },
              waypoints: wpList.map((w) => ({
                location: new g.maps.LatLng(w.lat, w.lng),
                stopover: true,
              })),
              travelMode: g.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (!alive) return;
              if (status !== 'OK' || !result?.routes?.[0]) {
                setError('Route not found');
                return;
              }
              setError(null);
              dr.setDirections(result);
              const legs = result.routes[0].legs || [];
              const totalMeters = legs.reduce((sum, l) => sum + (l.distance?.value || 0), 0);
              const totalSecs = legs.reduce((sum, l) => sum + (l.duration?.value || 0), 0);
              const km = +(totalMeters / 1000).toFixed(1);
              const min = Math.max(1, Math.round(totalSecs / 60));
              // Keep any distance/duration the caller supplied (the stored,
              // address-accurate figures) — the recomputed route can differ
              // when only approximate coordinates are available.
              setStats({
                distanceKm: distanceKm ?? km,
                durationMin: durationMin ?? min,
              });
              onRouteComputed?.({
                distance_km: km,
                duration_min: min,
                route_geometry: result.routes[0].overview_polyline || null,
              });
            }
          );
        }
      })
      .catch(() => setError('Map failed to load'));

    return () => {
      alive = false;
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      if (polylineRef.current) polylineRef.current.setMap(null);
      if (directionsRendererRef.current) directionsRendererRef.current.setMap(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng, geometry, JSON.stringify((waypoints || []).map(w => [w?.lat, w?.lng]))]);

  if (!HAS_GOOGLE_MAPS) {
    return (
      <div className="w-full bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
        Map disabled — Google Maps key not configured.
      </div>
    );
  }
  if (!pickup?.lat || !dropoff?.lat) {
    return (
      <div className="w-full bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-500" data-testid="route-map-empty">
        Select pickup and drop-off addresses to preview the route on the map.
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="route-map">
      <div
        ref={containerRef}
        className="w-full rounded-md overflow-hidden border border-gray-200"
        style={{ height }}
        data-testid="route-map-canvas"
      />
      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#2E7D32]/10 text-[#1B5E20] font-semibold rounded-sm" data-testid="route-distance">
          <Navigation className="w-4 h-4" />
          {stats.distanceKm != null ? `${stats.distanceKm} km` : 'Calculating…'}
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-800 font-semibold rounded-sm" data-testid="route-duration">
          <Clock className="w-4 h-4" />
          {stats.durationMin != null ? `${formatDuration(stats.durationMin)} drive` : 'Calculating…'}
        </span>
        {error && <span className="text-xs text-red-600">{error}</span>}
      </div>
    </div>
  );
};

// SVG-pin marker icon factory (matches Mapbox A/B styling for visual continuity).
function makeMarkerIcon(color, letter) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
      <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 22 14 22s14-11.5 14-22C28 6.27 21.73 0 14 0z" fill="${color}"/>
      <circle cx="14" cy="14" r="7" fill="white"/>
      <text x="14" y="18" text-anchor="middle" font-family="Helvetica, Arial, sans-serif" font-size="10" font-weight="700" fill="${color}">${letter}</text>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
    // eslint-disable-next-line no-undef
    scaledSize: new google.maps.Size(28, 36),
    // eslint-disable-next-line no-undef
    anchor: new google.maps.Point(14, 36),
  };
}

export default RouteMap;
